// Environment variable compatibility layer
// Uses Vite's import.meta.env for environment variables

export const getEnvironment = () => {
  // Use Vite environment variables
  return import.meta.env.VITE_APP_ENVIRONMENT || 'development';
};

export const getBackendUrl = () => {
  // Use Vite environment variables
  const url = import.meta.env.VITE_ELEMENTS_BACKEND_URL;

  if (!url) {
    console.warn('⚠️  No backend URL configured. Check environment variables.');
    return '';
  }

  return url;
};

// Helper for development fallback element IDs
export const getDevelopmentElementId = (defaultId) => {
  const environment = getEnvironment();
  return environment === 'development' ? defaultId : null;
};

// Helper to check if we're in development mode
export const isDevelopment = () => {
  return getEnvironment() === 'development';
};

// Helper to check if we're in production mode
export const isProduction = () => {
  return getEnvironment() === 'production';
};

// Helper to get image URL
export const getImageUrl = () => {
  const url = import.meta.env.VITE_IMAGE_URL;
  if (!url) {
    console.warn('⚠️ VITE_IMAGE_URL is not configured!');
    return '';
  }
  return url.replace(/\/$/, ''); // Remove trailing slash
};

export const getGlobalImageUrl = () => {
  const url = import.meta.env.VITE_GLOBAL_IMAGE_URL;
  if (!url) {
    console.warn('⚠️ VITE_GLOBAL_IMAGE_URL is not configured!');
    return '';
  }
  return url.replace(/\/$/, ''); // Remove trailing slash
};

// CON-313: the published rate table (fx/latest.json). Empty = every form shows
// USD. Read at call time so a test can `vi.stubEnv` it. No warning when empty:
// empty is the intended production value until the pilot gate (SPEC §2.9).
export const getFxRatesUrl = () => import.meta.env.VITE_FX_RATES_URL || '';

// Helper to build API URLs with proper error handling
export const buildApiUrl = (endpoint, params = {}) => {
  const baseUrl = getBackendUrl();
  if (!baseUrl) {
    console.error('Cannot build API URL: no backend URL configured');
    return '';
  }

  const url = new URL(endpoint, baseUrl);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
};

// For debugging Vite environment variables (if needed)
export const logEnvVars = () => {
  console.log('🔍 Environment:', getEnvironment());
  console.log('🔍 Backend URL:', getBackendUrl());

  // Critical check
  if (!import.meta.env.VITE_ELEMENTS_BACKEND_URL) {
    console.error('🚨 CRITICAL: VITE_ELEMENTS_BACKEND_URL is missing!');
    console.error('🚨 This will cause API calls to fail or use wrong endpoints');
  }
};

// Enhanced debugging utility for API responses
export const logApiResponse = (componentName, endpoint, data, elementID = null) => {
  console.group(`🔍 ${componentName} API Response`);
  console.log('📍 Endpoint:', endpoint);
  if (elementID) console.log('🆔 Element ID:', elementID);
  console.log('📊 Data received:', data);
  console.log('📝 Data keys:', Object.keys(data || {}));
  console.groupEnd();
};

// Utility to log API errors with context
export const logApiError = (componentName, error, context = {}) => {
  console.group(`❌ ${componentName} API Error`);
  console.error('Error:', error.message);
  console.error('Stack:', error.stack);
  console.log('Context:', context);
  console.groupEnd();
};
